import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IZoomDAO, IZoomUser } from '../../../api/zoom.dao';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { IRouter } from '../../../infrastructure/router';
import { RouteParams } from '../../../routes-table';
import { PagingViewModel } from '../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { IZoomUsersManagementRowProps } from './_zoom-users-management-row';

class ZoomUsersManagementViewModel {
    @mobx.observable
    private addedUsers: IZoomUser[];

    @mobx.observable
    private removedUserIds: string[];

    /**
     * store information about visible users in form of IZoomUsersManagementRowProps for performance sake
     * this is done in order to apply updates to particular visible user record avoiding re-scan of all records in table
     * might be changed to store the list of IZoomUser and convert it to IZoomUsersManagementRowProps in 'list' component
     * if current implementation become tricky.
     */
    @mobx.observable
    private visibleUsers: IZoomUsersManagementRowProps[];

    @mobx.observable
    private fetchUsersError: string;

    @mobx.observable
    private saveChangesError: string;

    @mobx.observable
    private pagingVM: PagingViewModel;

    constructor(
        @IoC.Inject private readonly zoomDao: IZoomDAO,
        @IoC.Inject private readonly errorParsing: IErrorParsing,
        @IoC.Inject private readonly router: IRouter<RouteParams>,
    ) {
        this.resetSelection();
        this.initialize();
    }

    public getVisibleUsers() {
        return this.visibleUsers;
    }

    public getAddedUsers() {
        return this.addedUsers as Readonly<IZoomUser[]>;
    }

    public getRemovedUsers() {
        return this.removedUserIds as Readonly<string[]>;
    }

    public getFetchError() {
        return this.fetchUsersError;
    }

    public getSaveChangesError() {
        return this.saveChangesError;
    }

    public getPagingVM() {
        return this.pagingVM;
    }

    @mobx.action
    public addUser(zoomUser: IZoomUser) {
        const addedUserIndex = this.addedUsers.findIndex(x => x.id === zoomUser.id);
        if (addedUserIndex !== -1) {
            return;
        }

        const removedIndex = this.removedUserIds.indexOf(zoomUser.id);
        if (removedIndex === -1) {
            this.addedUsers.push(zoomUser);
        } else {
            this.removedUserIds.splice(removedIndex, 1);
        }
        this.updateIndividualRowStatus(zoomUser.id);
    }

    @mobx.action
    public initialize() {
        this.pagingVM = new PagingViewModel();
        mobx.reaction(this.pagingVM.getCurrentPage, this.fetchUsersPage);
        mobx.runInAction(() => this.fetchUsersPage());
    }

    @mobx.action
    public removeUser(zoomUserId: string) {
        const removedIndex = this.removedUserIds.indexOf(zoomUserId);
        if (removedIndex !== -1) {
            return;
        }

        const addedUserIndex = this.addedUsers.findIndex(x => x.id === zoomUserId);
        if (addedUserIndex === -1) {
            this.removedUserIds.push(zoomUserId);
        } else {
            this.addedUsers.splice(addedUserIndex, 1);
        }
        this.updateIndividualRowStatus(zoomUserId);
    }

    @mobx.action
    public fetchUsersPage = async () => {
        this.fetchUsersError = '';
        // need to create flag like isLoading instead of check and nulling this variable;
        this.visibleUsers = null;

        try {
            const userRes = await this.zoomDao.getZoomUsers(this.pagingVM.getPageSize(), this.pagingVM.getCurrentPage());
            const usersApiDTOList = userRes.users;
            mobx.runInAction(() => {
                this.visibleUsers = usersApiDTOList.map(x => this.convertAPIDTOsToTableRow(x));
                this.pagingVM.setTotalRecords(userRes.total_records);
            });
        } catch (e) {
            const apiError = this.errorParsing.extractAPIError(e);
            if (apiError != null && apiError.errorCode === 500) {
                mobx.runInAction(() => {
                    this.fetchUsersError = apiError.errorMessage;
                });
            } else {
                throw e;
            }
        }
    };

    public saveUserManagementChanges = async () => {
        try {
            await this.zoomDao.updateTrackedUsers({
                addItems: this.addedUsers.map(x => {
                    return {
                        firstName: x.first_name,
                        lastName: x.last_name,
                        zoomId: x.id,
                        email: x.email,
                        groups: x.groups
                    };
                }),
                deleteItems: this.removedUserIds
            });
            this.resetSelection();
        } catch (e) {
            // in case of update failure we don't know whether update has been processed by backend, so reset data from server
            const apiError = this.errorParsing.extractMessage(e);
            this.handleSaveChangesError(apiError);
            throw e;
        }
    };

    @mobx.action
    public cleanup() {
        this.visibleUsers = null;
        this.resetSelection();
        this.fetchUsersError = null;
    }

    @mobx.action
    public resetPagingVM() {
        this.pagingVM.selectPage(1);
    }

    @mobx.action
    public resetSelection() {
        this.addedUsers = [];
        this.removedUserIds = [];
        this.updateVisibleRowsStatus();
    }

    public isUserSelected(zoomUser: IZoomUser) {
        return !!this.addedUsers.find(x => x.id === zoomUser.id) ||
            (zoomUser.tracked && this.removedUserIds.indexOf(zoomUser.id) === -1);
    }

    @mobx.action
    private handleSaveChangesError(errorMessage) {
        this.saveChangesError = errorMessage;
    }

    @mobx.action
    private receiveNewTrackedUsers() {
        // for now - just reset selection once tracked users are updated.
        // if we decide to preserve it at some point - recalculate added/removed user arrays here
        this.resetSelection();

        this.updateVisibleRowsStatus();
    }

    private convertAPIDTOsToTableRow(apiObject: IZoomUser) : IZoomUsersManagementRowProps {
        const isTracked = this.isUserSelected(apiObject);

        return {
            zoomUser: apiObject,
            isTracked: isTracked,
            toggleSelection: isTracked ?
                () => this.removeUser(apiObject.id) :
                () => this.addUser(apiObject),
            pendingStartTracking: !!this.addedUsers.find(x => x.id === apiObject.id),
            pendingStopTracking: this.removedUserIds.indexOf(apiObject.id) !== -1,
        };
    }

    @mobx.action
    private updateIndividualRowStatus(userIdToUpdate: string) {
        const visibleUserIndex = this.visibleUsers.findIndex(x => x.zoomUser.id === userIdToUpdate);
        if (visibleUserIndex === -1) {
            return;
        }

        const currentTableRow = this.visibleUsers[visibleUserIndex];
        const newTableRow = this.convertAPIDTOsToTableRow(currentTableRow.zoomUser);
        currentTableRow.isTracked = newTableRow.isTracked;
        currentTableRow.toggleSelection = newTableRow.toggleSelection;
        currentTableRow.pendingStopTracking = newTableRow.pendingStopTracking;
        currentTableRow.pendingStartTracking = newTableRow.pendingStartTracking;
    }

    private updateVisibleRowsStatus() {
        if (this.visibleUsers) {
            for (const user of this.visibleUsers) {
                this.updateIndividualRowStatus(user.zoomUser.id);
            }
        }
    }
}

export { ZoomUsersManagementViewModel };